import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useDragLayer, XYCoord } from "react-dnd";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";

type Props = {
  enabled: boolean;
  isMobile?: boolean;
};

function getLayerItemStyles(currentOffset: XYCoord | null) {
  if (!currentOffset) {
    return { display: "none" as const };
  }

  const { x, y } = currentOffset;
  return {
    transform: `translate(${x}px, ${y}px)`,
  };
}

const ApproverPreviewCard = ({
  color,
  name,
  email,
  isMobile,
  previewWidth,
}: {
  color?: string;
  name?: string;
  email?: string;
  isMobile?: boolean;
  previewWidth?: string;
}) => {
  const hasName = Boolean(name && String(name).trim());
  const hasEmail = Boolean(email && String(email).trim());

  return (
    <Paper
      elevation={3}
      sx={{
        width: previewWidth ?? (isMobile ? "min(92vw, 560px)" : "min(560px, 42vw)"),
        borderRadius: "8px",
        border: "1px solid #ddd",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            height: "6px",
            width: "100%",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            backgroundColor: color,
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            width: "8px",
            top: 0,
            bottom: 0,
            left: 0,
            backgroundColor: color,
            borderRadius: "8px 0px 0px 8px",
          }}
        />
      )}

      <Box sx={{ p: 1.25, ...(isMobile ? {} : { pl: 2.5, position: "relative" }) }}>
        <Stack spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.2,
              color: hasName ? "text.primary" : "text.disabled",
            }}
            noWrap
          >
            {hasName ? name : "Enter Name"}
          </Typography>
          <Typography
            variant="body2"
            color={hasEmail ? "text.secondary" : "text.disabled"}
            noWrap
          >
            {hasEmail ? email : "Enter Email"}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export default function ApproverDragLayer({ enabled, isMobile }: Props) {
  const { signRequest } = useAppSelector((state: RootState) => state.app);
  const cardPreviewWidth = isMobile
    ? "clamp(220px, calc(100vw - 24px), 560px)"
    : "clamp(300px, 40vw, 560px)";
  const groupPreviewWidth = isMobile
    ? "clamp(240px, calc(100vw - 16px), 600px)"
    : "clamp(320px, 42vw, 600px)";

  const { isDragging, itemType, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem() as any,
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!enabled || !isDragging || itemType !== "approver") {
    return null;
  }

  const dragType = item?.dragType;
  let previewApprovers: any[] = [];

  if (dragType === "SINGLE") {
    const reviewer =
      signRequest?.approval?.reviewers?.find((r: any) => r.id === item?.id) ?? null;
    previewApprovers = reviewer ? [reviewer] : [];
  } else if (dragType === "GROUP") {
    const groupIds: number[] = item?.groupReviewerIds ?? [];
    previewApprovers = groupIds
      .map((id) => signRequest?.approval?.reviewers?.find((r: any) => r.id === id))
      .filter(Boolean);
  } else if (dragType === "GROUP_MEMBER") {
    const reviewer =
      signRequest?.approval?.reviewers?.find((r: any) => r.id === item?.signerId) ??
      null;
    previewApprovers = reviewer ? [reviewer] : [];
  }

  if (!previewApprovers.length) {
    return null;
  }


  const hasGroupName = Boolean(item?.groupName && String(item?.groupName).trim());

  return (
    <Box
      sx={{
        position: "fixed",
        pointerEvents: "none",
        zIndex: 2000,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          ...getLayerItemStyles(currentOffset),
          willChange: "transform",
          transformOrigin: "0 0",
        }}
      >
        {dragType === "GROUP" ? (
          <Paper
            elevation={4}
            sx={{
              p: 1,
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#F8FAFC",
              width: groupPreviewWidth,
              maxWidth: "calc(100vw - 12px)",
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  px: 1,
                  color: "text.primary",
                }}
                noWrap
              >
                {hasGroupName ? item?.groupName : "Enter Group Name"}
              </Typography>
              {previewApprovers.map((reviewer: any) => (
                <ApproverPreviewCard
                  key={reviewer.id}
                  isMobile={isMobile}
                  previewWidth="100%"
                  color={reviewer.color}
                  name={reviewer.name}
                  email={reviewer.email}
                />
              ))}
            </Stack>
          </Paper>
        ) : (
          <Box sx={{ width: cardPreviewWidth, maxWidth: "calc(100vw - 16px)" }}>
            <Stack spacing={1}>
              {previewApprovers.map((reviewer: any) => (
                <ApproverPreviewCard
                  key={reviewer.id}
                  isMobile={isMobile}
                  previewWidth="100%"
                  color={reviewer.color}
                  name={reviewer.name}
                  email={reviewer.email}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

